import { useState } from "react";
import {
  MachineListGroupedByShop,
  MachineDetails,
  CreateMachine,
  UpdateMachine,
  DeleteMachine
} from "@/hooks/services/pps/handlers/machines-api";
import {
  useGetData,
  usePostData,
  usePutData,
  useDeleteData,
  useInvalidateQueries
} from "@/hooks/services/use-query-hooks";
import { useWorkSpaceStore } from "@/stores/store";

interface UseMachinesParams {
  page?: number;
  pageSize?: number;
  initialSearch?: string;
  workspaceId?: number;
}

interface UseMachinesReturn {
  data: any;
  allMachines: any[];
  filteredShops: any[];
  nonOperationalMachines: any[];

  total: number;
  totalPages: number;

  isLoading: boolean;
  error: any;

  searchQuery: string;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;

  refetch: () => void;

  collapsedShops: Set<string>;
  toggleShopCollapse: (shopId: number) => void;
  expandAllShops: () => void;
  collapseAllShops: () => void;
}

export const useMachines = ({
  page = 1,
  pageSize = 50,
  initialSearch = "",
  workspaceId
}: UseMachinesParams = {}): UseMachinesReturn => {
  const { workspace } = useWorkSpaceStore();
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [collapsedShops, setCollapsedShops] = useState<Set<string>>(new Set());

  const effectiveWorkspaceId = workspaceId ?? workspace?.id ?? 0;

  const { data, refetch, isLoading, error } = useGetData(
    MachineListGroupedByShop({
      workspaceId: effectiveWorkspaceId,
      page,
      pageSize,
      search: searchQuery
    })
  );

  const allMachines = data?.data?.flatMap((shop: any) => shop.machines) || [];

  const nonOperationalMachines = allMachines.filter(
    (machine: any) =>
      machine.status.name === "Maintenance" ||
      machine.status.name === "Unavailable"
  );

  const filteredShops =
    data?.data
      ?.map((shop: any) => ({
        ...shop,
        machines: shop.machines.filter(
          (machine: any) => machine.status.name === "Available"
        )
      }))
      .filter((shop: any) => shop.machines.length > 0) || [];

  const toggleShopCollapse = (shopId: number) => {
    const shopIdStr = shopId.toString();
    const newCollapsed = new Set(collapsedShops);
    if (newCollapsed.has(shopIdStr)) {
      newCollapsed.delete(shopIdStr);
    } else {
      newCollapsed.add(shopIdStr);
    }
    setCollapsedShops(newCollapsed);
  };

  const expandAllShops = () => {
    setCollapsedShops(new Set());
  };

  const collapseAllShops = () => {
    const shopIds =
      data?.data?.map((shop: any) => shop.shopId.toString()) || [];
    setCollapsedShops(new Set(shopIds));
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return {
    data,
    allMachines,
    filteredShops,
    nonOperationalMachines,

    total: data?.total ?? 0,
    totalPages: data?.pages ?? 0,

    isLoading,
    error,

    searchQuery,
    setSearchQuery,
    clearSearch,

    refetch,

    collapsedShops,
    toggleShopCollapse,
    expandAllShops,
    collapseAllShops
  };
};

export const useMachineStats = (workspaceId?: number) => {
  const { allMachines, nonOperationalMachines, data } = useMachines({
    pageSize: 100,
    workspaceId
  });

  const totalMachines = allMachines.length;
  const availableMachines = allMachines.filter(
    (machine: any) => machine.status.name === "Available"
  ).length;
  const maintenanceMachines = allMachines.filter(
    (machine: any) => machine.status.name === "Maintenance"
  ).length;
  const unavailableMachines = allMachines.filter(
    (machine: any) => machine.status.name === "Unavailable"
  ).length;

  const totalShops = data?.data?.length || 0;
  const shopsWithIssues =
    data?.data?.filter(
      (shop: any) => shop.maintenanceCount > 0 || shop.unavailableCount > 0
    ).length || 0;

  return {
    totalMachines,
    availableMachines,
    maintenanceMachines,
    unavailableMachines,
    nonOperationalCount: nonOperationalMachines.length,
    totalShops,
    shopsWithIssues,
    operationalRate:
      totalMachines > 0 ? (availableMachines / totalMachines) * 100 : 0
  };
};

export const useMachinesByStatus = (status: string, workspaceId?: number) => {
  const { allMachines, isLoading, error, refetch } = useMachines({
    workspaceId
  });

  const machinesByStatus = allMachines.filter(
    (machine: any) => machine.status.name === status
  );

  return {
    machines: machinesByStatus,
    count: machinesByStatus.length,
    isLoading,
    error,
    refetch
  };
};

export const useMachine = (
  id: string | number,
  options?: { enabled?: boolean }
) => {
  const { data, isLoading, error, refetch } = useGetData(
    MachineDetails(String(id)),
    { enabled: options?.enabled ?? true }
  );

  return {
    machine: data?.data,
    isLoading,
    error,
    refetch,

    refetchMachine: refetch
  };
};

export const useMachineOperations = () => {
  const invalidate = useInvalidateQueries();

  const createMutation = usePostData(CreateMachine());
  const updateMutation = usePutData(UpdateMachine());
  const deleteMutation = useDeleteData(DeleteMachine());

  const createMachine = async (data: any) => {
    const result = await createMutation.mutateAsync(data, {
      onSettled: () => {
        invalidate([
          ["MachineListGroupedByShop"],
          ["TechnicianGroupedByShop"],
          ["JobSchedule"],
          ["TechnicianSchedule"],
          ["MachineSchedule"]
        ]);
      }
    });

    return result;
  };

  const updateMachine = async (data: any) => {
    const result = await updateMutation.mutateAsync(data, {
      onSettled: () => {
        const keys: string[][] = [
          ["MachineListGroupedByShop"],
          ["TechnicianGroupedByShop"],
          ["JobSchedule"],
          ["TechnicianSchedule"],
          ["MachineSchedule"]
        ];
        if (data.machineId) {
          keys.push(["MachineDetails", String(data.machineId)]);
        }
        invalidate(keys);
      }
    });

    return result;
  };

  const deleteMachine = async (id: string) => {
    const result = await deleteMutation.mutateAsync(id, {
      onSettled: () => {
        invalidate([
          ["MachineListGroupedByShop"],
          ["TechnicianGroupedByShop"],
          ["JobSchedule"],
          ["TechnicianSchedule"],
          ["MachineSchedule"]
        ]);
      }
    });

    return result;
  };

  return {
    createMachine,
    updateMachine,
    deleteMachine,

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error
  };
};
